// SA-3
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_PRODUCTION_WORKERS } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  formatProductionWorkerContacts,
  productionWorkerActiveLabel,
} from '../../../lib/productionWorkers'
import { matchesListSearch } from '../../../lib/listSearch'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import type { ProductionWorkerRecord } from '../../../types/records'
import { PageHeader, PageShell, Alert, ButtonLink, SearchField } from '../../../components/ui'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

export function ProductionWorkersPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<ProductionWorkerRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_PRODUCTION_WORKERS).getFullList<ProductionWorkerRecord>({
        sort: 'name',
        expand: 'defaultShift',
      })
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    if (!window.confirm('Удалить сотрудника производства?')) return
    try {
      await pb.collection(COL_PRODUCTION_WORKERS).delete(id)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка удаления'))
    }
  }

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.name,
        row.position || '',
        row.expand?.defaultShift?.name || '',
        productionWorkerActiveLabel(row.active),
        row.notes || '',
        row.phone || '',
        row.email || '',
        row.hourlyRate != null ? String(row.hourlyRate) : '',
      ),
    )
  }, [items, searchQuery])

  return (
    <PageShell>
      <PageHeader
        title="Сотрудники производства"
        lead="Справочник работников цеха без входа в систему — для табеля и экрана смены."
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE) && (
          <ButtonLink to="/admin/administration/production-workers/new" variant="primary">
            Добавить сотрудника
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по ФИО, должности, контактам…"
            aria-label="Поиск сотрудников производства"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>ФИО</th>
              <th className={tableHeadClass}>Должность</th>
              <th className={tableHeadClass}>Смена по умолчанию</th>
              <th className={tableHeadClass}>Контакты</th>
              <th className={tableHeadClass}>Ставка, ₽/ч</th>
              <th className={tableHeadClass}>Статус</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={7}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={7}>
                  <em>
                    {items.length === 0
                      ? 'Нет сотрудников — добавьте первого или дождитесь применения миграции.'
                      : 'Ничего не найдено'}
                  </em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{row.name}</td>
                  <td className={tableCellClass}>{row.position || '—'}</td>
                  <td className={tableCellClass}>{row.expand?.defaultShift?.name || '—'}</td>
                  <td className={tableCellClass}>{formatProductionWorkerContacts(row)}</td>
                  <td className={tableCellClass}>
                    {row.hourlyRate != null ? row.hourlyRate.toLocaleString('ru-RU') : '—'}
                  </td>
                  <td className={tableCellClass}>{productionWorkerActiveLabel(row.active)}</td>
                  <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                    {can(PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE) && (
                      <Link
                        to={`/admin/administration/production-workers/${row.id}`}
                        className={btn('ghost')}
                      >
                        Изменить
                      </Link>
                    )}
                    {can(PERMISSION_KEYS.PRODUCTION_WORKERS_DELETE) && (
                      <button
                        type="button"
                        className={cn(btn('danger'), 'ml-1')}
                        onClick={() => void removeRow(row.id)}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
